import { nextTick } from 'vue'
import defaultSettings from '@/settings'
import { useDark, useToggle, useStorage } from '@vueuse/core'
import { useDynamicTitle } from '@/utils/dynamicTitle'
import eventBus from '@/utils/eventBus'

const isDark = useDark()
const toggleDark = useToggle(isDark)
const currentTheme = useStorage('current-theme', 'theme-light') // 默认主题
const { sideTheme, showSettings, topNav, tagsView, fixedHeader, sidebarLogo, dynamicTitle } = defaultSettings

const storageSetting = JSON.parse(localStorage.getItem('pinia-settings')) || ''

// 预编译正则表达式
const VAR_REGEX = /^var\((--[\w-]+)(,\s*(.*))?\)$/

const useSettingsStore = defineStore(
  'settings',
  {
    persist: {
      key: 'pinia-settings',
      // storage: sessionStorage,
    },
    state: () => ({
      title: '',
      theme: storageSetting.theme || '#409EFF',
      sideTheme: storageSetting.sideTheme || sideTheme,
      showSettings: showSettings,
      topNav: storageSetting.topNav === undefined ? topNav : storageSetting.topNav,
      tagsView: storageSetting.tagsView === undefined ? tagsView : storageSetting.tagsView,
      fixedHeader: storageSetting.fixedHeader === undefined ? fixedHeader : storageSetting.fixedHeader,
      sidebarLogo: storageSetting.sidebarLogo === undefined ? sidebarLogo : storageSetting.sidebarLogo,
      dynamicTitle: storageSetting.dynamicTitle === undefined ? dynamicTitle : storageSetting.dynamicTitle,
      isDark: isDark.value,
      currentTheme: currentTheme.value || "theme-light",
      vars: "",
    }),
    getters: {
      var: (state) => {
        // 缓存 parsed 结果，避免重复 JSON.parse
        let parsedCache = null
        let cachedVars = ''

        return (name, defaultValue = '') => {
          // 只有 vars 变化时才重新 parse
          if (cachedVars !== state.vars) {
            parsedCache = state.vars ? JSON.parse(state.vars) : {}
            cachedVars = state.vars
          }

          let value = parsedCache[name] || defaultValue
          if (!value) return value

          // 如果不是 var 引用，直接返回
          if (typeof value !== 'string' || !value.startsWith('var(')) {
            return value
          }

          // 递归解析 var(--xxx) 引用
          const visited = new Set()
          while (value && typeof value === 'string' && value.startsWith('var(')) {
            const match = value.match(VAR_REGEX)
            if (!match) break

            const varName = match[1]
            if (visited.has(varName)) break // 防止循环引用
            visited.add(varName)

            const actualValue = parsedCache[varName]
            if (!actualValue) break

            value = actualValue
          }

          return value
        }
      },
    },
    actions: {
      // 页面加载后应用主题的方法
      initThemeOnLoad() {
        // 页面加载时应用保存的主题
        const savedTheme = currentTheme.value;
        this.switchTheme(savedTheme);
        this.toggleTheme(savedTheme == "theme-dark" ? { isDark: true } : { isDark: false })
      },
      // 修改布局设置
      changeSetting(data) {
        const { key, value } = data
        if (this.hasOwnProperty(key)) {
          this[key] = value
        }
      },
      // 设置网页标题
      setTitle(title) {
        this.title = title
        useDynamicTitle()
      },
      // 切换主题
      switchTheme(themeName) {
        // 移除旧主题类
        document.documentElement.className = document.documentElement.className
          .replace(/theme-\w+/g, '')

        // 添加新主题类
        document.documentElement.classList.add(`${themeName}`)

        // 更新状态
        this.currentTheme = themeName
        currentTheme.value = themeName

        // 获取新的 CSS 变量
        nextTick(() => {
          this.fetchVars()
        })
      },
      // 切换暗黑模式
      toggleTheme(data) {
        if (data && data.isDark !== undefined) {
          // 如果传入了具体值且与当前状态不同，则切换
          if (data.isDark !== isDark.value) {
            toggleDark()
          }
        } else {
          // 如果没有传入值，则切换当前状态
          toggleDark()
        }

        // 使用 isDark.value 更新 store 状态
        this.isDark = isDark.value
        this.switchTheme(isDark.value ? 'theme-dark' : 'theme-light');
        // 获取所有CSS变量并存储
        nextTick(() => {
          this.fetchVars()
        })
      },
      // 获取所有CSS变量并存储
      fetchVars() {
        const variables = {}
        const currentThemeClass = document.documentElement.className.match(/theme-\w+/)?.[0] || ''

        // 遍历所有样式表获取CSS变量定义
        const sheets = document.styleSheets
        try {
          for (let i = 0; i < sheets.length; i++) {
            const sheet = sheets[i]
            try {
              const rules = sheet.cssRules || sheet.rules
              if (!rules) continue

              for (let j = 0; j < rules.length; j++) {
                const rule = rules[j]
                // 检查规则是否匹配当前主题
                const ruleText = rule.cssText || ''
                const matchesTheme = ruleText.includes(currentThemeClass)

                if (rule.style && rule.style.length > 0) {
                  for (let k = 0; k < rule.style.length; k++) {
                    const name = rule.style[k]
                    if (name.startsWith('--')) {
                      const value = rule.style.getPropertyValue(name).trim()
                      // 如果匹配当前主题，或变量尚未设置，则设置值
                      // 主题匹配的规则优先级更高
                      if (matchesTheme) {
                        if (value) {
                          variables[name] = value
                        }
                      } else if (!variables[name] && value) {
                        // 非主题规则只在变量未设置时设置
                        variables[name] = value
                      }
                    }
                  }
                }
              }
            } catch (e) {
              // 跨域样式表可能会抛出异常，跳过
              continue
            }
          }
        } catch (e) {
          console.warn('获取样式表失败:', e)
        }

        this.vars = JSON.stringify(variables)

        // 通知所有图表组件重新渲染
        eventBus.emit('theme-change')
        return variables
      },
      // 重置按钮的颜色
      getResetBtnColor() {
        return this.var('--color8')
      },
      getColorByName(colorName) {
        return this.var(colorName)
      },
      // 获取多个颜色的批量方法
      getThemeColors() {
        return {
          resetBtnColor: this.var('--color8'),//重置按钮颜色
          //  primaryColor: computed(() => settingsStore.var('--primary-color')),
        }
      },

    },
  })

export default useSettingsStore
